<script lang="ts">
  import { onDestroy, type Snippet } from 'svelte';
  import { playbackOrder } from '../anim/export';
  import type { FrameStore } from '../anim/frames';
  import { t } from '../i18n/index.svelte';

  let {
    store,
    fps,
    loop,
    revision = 0,
    children,
  }: {
    store: FrameStore | null;
    fps: number;
    loop: 'loop' | 'ping_pong';
    revision?: number;
    children?: Snippet;
  } = $props();

  let canvas: HTMLCanvasElement;
  let playing = $state(false);
  let pos = $state(0);
  let fit = $state(true);
  let raf = 0;
  let last = 0;

  const order = $derived(store ? playbackOrder(store.count, loop) : []);
  const frame = $derived(order[Math.min(pos, Math.max(0, order.length - 1))] ?? 0);

  function draw(i: number) {
    if (!store || !canvas) return;
    const px = store.rgba(i);
    if (!px) return;
    if (canvas.width !== store.width || canvas.height !== store.height) {
      canvas.width = store.width;
      canvas.height = store.height;
    }
    canvas.getContext('2d')!.putImageData(new ImageData(px, store.width, store.height), 0, 0);
  }

  $effect(() => {
    void revision;
    draw(frame);
  });

  function tick(now: number) {
    if (!playing) return;
    if (now - last >= 1000 / fps) {
      last = now;
      pos = (pos + 1) % Math.max(1, order.length);
    }
    raf = requestAnimationFrame(tick);
  }

  function toggle() {
    playing = !playing;
    if (playing) {
      last = performance.now();
      raf = requestAnimationFrame(tick);
    } else cancelAnimationFrame(raf);
  }

  function step(d: number) {
    playing = false;
    cancelAnimationFrame(raf);
    pos = (pos + d + order.length) % Math.max(1, order.length);
  }

  let lastCount = -1;
  $effect(() => {
    // New result: rewind and autoplay. Switching views of the same clip keeps position.
    const n = store?.count ?? -1;
    if (n !== lastCount) {
      lastCount = n;
      if (store) {
        pos = 0;
        if (!playing) toggle();
      }
    }
  });
  $effect(() => {
    void store;
    draw(frame);
  });

  onDestroy(() => cancelAnimationFrame(raf));
</script>

<div class="player">
  <div class="stage" class:fit>
    <canvas bind:this={canvas} data-testid="player-canvas" class:hidden={!store}></canvas>
  </div>
  {#if store}
    <div class="controls">
      <button onclick={() => step(-1)} title={t('player.prev')} aria-label={t('player.prev')}>⏮</button>
      <button
        class="play"
        onclick={toggle}
        data-testid="player-toggle"
        aria-label={playing ? t('player.pause') : t('player.play')}
      >
        {playing ? '⏸' : '▶'}
      </button>
      <button onclick={() => step(1)} title={t('player.next')} aria-label={t('player.next')}>⏭</button>
      <input
        type="range"
        min="0"
        max={Math.max(0, order.length - 1)}
        bind:value={pos}
        oninput={() => (playing = false)}
      />
      <span class="mono muted">{frame + 1}/{store.count} · {fps} fps</span>
      {@render children?.()}
      <div class="seg">
        <button class:on={fit} onclick={() => (fit = true)}>{t('preview.fit')}</button>
        <button class:on={!fit} onclick={() => (fit = false)}>1:1</button>
      </div>
    </div>
  {/if}
</div>

<style>
  .player {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .stage {
    flex: 1;
    min-height: 0;
    overflow: auto;
    display: grid;
    place-items: center;
    padding: 16px;
    background: repeating-conic-gradient(#14161b 0% 25%, #111317 0% 50%) 50% / 24px 24px;
  }
  .stage.fit canvas {
    max-width: 100%;
    max-height: 100%;
  }
  .stage:not(.fit) {
    place-items: start;
  }
  canvas {
    box-shadow: 0 4px 30px #0008;
  }
  .hidden {
    display: none;
  }
  .controls {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 12px;
    border-top: 1px solid var(--line);
    background: var(--panel);
  }
  .controls input[type='range'] {
    flex: 1;
  }
  .play {
    width: 44px;
  }
</style>
